import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserNotifications } from "../services/notificationService";

const normalizeNotifications = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const getNotificationId = (notification, index) =>
  notification.id ||
  notification._id ||
  notification.notificationId ||
  `${notification.title || "notification"}-${notification.createdAt || notification.created_at || index}`;

const getNotificationMessage = (notification) =>
  notification.message ||
  notification.body ||
  notification.description ||
  notification.text ||
  "";

const getPayload = (notification) => notification.data || notification.payload || {};

const getNotificationType = (notification) =>
  String(
    notification.type ||
      notification.notificationType ||
      notification.notification_type ||
      getPayload(notification).type ||
      getPayload(notification).notificationType ||
      "",
  ).toLowerCase();

const getOrderId = (notification) => {
  const payload = getPayload(notification);
  const order =
    notification.order ||
    notification.orderDetails ||
    payload.order ||
    payload.orderDetails ||
    {};

  return (
    notification.orderId ||
    notification.order_id ||
    notification.referenceId ||
    notification.reference_id ||
    payload.orderId ||
    payload.order_id ||
    order.id ||
    order._id ||
    order.orderId
  );
};

const getKupatHolimRequestId = (notification) => {
  const payload = getPayload(notification);
  const request =
    notification.kupatHolimRequest ||
    notification.request ||
    payload.kupatHolimRequest ||
    payload.request ||
    {};

  return (
    notification.kupatHolimRequestId ||
    notification.kupat_holim_request_id ||
    notification.requestId ||
    notification.request_id ||
    payload.kupatHolimRequestId ||
    payload.kupat_holim_request_id ||
    payload.requestId ||
    payload.request_id ||
    request.id ||
    request._id ||
    (getNotificationType(notification).includes("kupat")
      ? notification.referenceId || notification.reference_id
      : null)
  );
};

const getConversationId = (notification) => {
  const payload = getPayload(notification);
  const message = notification.normalizedMessage || notification.messageData || payload.message || {};

  return (
    notification.conversationId ||
    notification.conversation_id ||
    payload.conversationId ||
    payload.conversation_id ||
    message.conversationId ||
    message.conversation_id
  );
};

const getNotificationTarget = (notification) => {
  const type = getNotificationType(notification);
  const conversationId = getConversationId(notification);
  if (conversationId || type.includes("chat") || type.includes("message")) {
    return conversationId ? { kind: "chat", id: conversationId } : null;
  }

  const kupatHolimRequestId = getKupatHolimRequestId(notification);
  if (kupatHolimRequestId || type.includes("kupat")) {
    return kupatHolimRequestId
      ? { kind: "kupat", id: kupatHolimRequestId }
      : null;
  }

  const orderId = getOrderId(notification);
  return orderId ? { kind: "order", id: orderId } : null;
};

const isActionableNotification = (notification) => Boolean(getNotificationTarget(notification));

const notificationContent = (notification) => {
  const target = getNotificationTarget(notification);
  if (!target) return { title: "New notification", action: "Open notification", icon: "bi-arrow-up-right" };

  if (target.kind === "order") {
    return { title: `New order #${target.id}`, action: "Click to view order", icon: "bi-box-arrow-up-right" };
  }
  if (target.kind === "kupat") {
    return { title: "Kupat Holim approval request", action: "Click to review request", icon: "bi-clipboard2-check" };
  }
  return { title: "New chat message", action: "Click to open chat", icon: "bi-chat-dots" };
};

const getNotificationTime = (notification) => {
  const value =
    notification.createdAt ||
    notification.created_at ||
    notification.sentAt ||
    notification.sent_at ||
    notification.updatedAt ||
    notification.updated_at;

  if (!value) return "Just now";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Just now";

  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const isUnreadNotification = (notification) => {
  if (typeof notification.unread === "boolean") return notification.unread;
  if (typeof notification.isUnread === "boolean") return notification.isUnread;
  if (typeof notification.read === "boolean") return !notification.read;
  if (typeof notification.isRead === "boolean") return !notification.isRead;

  return !notification.readAt && !notification.read_at;
};

const READ_NOTIFICATION_IDS_STORAGE_KEY = "readAdminNotificationIds";

const loadReadNotificationIds = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(READ_NOTIFICATION_IDS_STORAGE_KEY) || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch (error) {
    return [];
  }
};

const Notification = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readNotificationIds, setReadNotificationIds] = useState(loadReadNotificationIds);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const unreadCount = notifications.filter(isUnreadNotification).length;
  const unreadCountLabel = unreadCount > 99 ? "99+" : unreadCount;

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getUserNotifications();
      setNotifications(
        normalizeNotifications(response)
          .filter(isActionableNotification)
          .map((notification, index) => {
            const notificationId = getNotificationId(notification, index);
            return readNotificationIds.includes(String(notificationId))
              ? { ...notification, unread: false, isUnread: false, read: true, isRead: true }
              : notification;
          }),
      );
    } catch (loadError) {
      setError(loadError.message || "Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [readNotificationIds]);

  useEffect(() => {
    loadNotifications();

    const interval = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [loadNotifications, open]);

  useEffect(() => {
    const handleLocalChatNotification = (event) => {
      const notification = event.detail;
      if (!notification || !isActionableNotification(notification)) return;

      setNotifications((previousNotifications) => [
        notification,
        ...previousNotifications.filter(
          (item, index) =>
            getNotificationId(item, index) !== getNotificationId(notification, 0),
        ),
      ]);
    };

    window.addEventListener("chat-notification", handleLocalChatNotification);
    return () => window.removeEventListener("chat-notification", handleLocalChatNotification);
  }, []);

  useEffect(() => {
    localStorage.setItem(
      READ_NOTIFICATION_IDS_STORAGE_KEY,
      JSON.stringify(readNotificationIds),
    );
  }, [readNotificationIds]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const openNotification = (notification, index) => {
    const target = getNotificationTarget(notification);
    if (!target) return;
    const notificationId = String(getNotificationId(notification, index));

    setNotifications((previousNotifications) =>
      previousNotifications.map((item, itemIndex) =>
        String(getNotificationId(item, itemIndex)) === notificationId
          ? { ...item, unread: false, isUnread: false, read: true, isRead: true }
          : item,
      ),
    );
    setReadNotificationIds((previousIds) =>
      previousIds.includes(notificationId)
        ? previousIds
        : [...previousIds, notificationId],
    );
    setOpen(false);
    if (target.kind === "order") {
      navigate(`/dashboard?order=${encodeURIComponent(target.id)}`);
    } else if (target.kind === "chat") {
      navigate(`/chat?conversation=${encodeURIComponent(target.id)}`);
    }
  };

  return (
    <div className="notification-wrapper" ref={dropdownRef}>
      <div className="notification">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : "Notifications"}
          aria-expanded={open}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-bell link-icon"
          >
            <path d="M10.268 21a2 2 0 0 0 3.464 0"></path>
            <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"></path>
          </svg>

          {unreadCount > 0 && (
            <span className="notification-count" aria-hidden="true">{unreadCountLabel}</span>
          )}
        </button>
      </div>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <div>
              <h6>Notifications</h6>
              <span>{unreadCount} unread</span>
            </div>
          </div>

          <div className="notification-list">
            {loading && (
              <>
                {[1, 2, 3].map((id) => (
                  <div
                    key={id}
                    className="notification-item notification-skeleton"
                  >
                    <div className="notification-item-icon skeleton-box" />
                    <div className="notification-item-content">
                      <div className="notification-item-title skeleton-box" />
                      <div className="notification-message skeleton-box" />
                    </div>
                  </div>
                ))}
              </>
            )}

            {!loading && error && (
              <div className="notification-empty-state is-error">
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && notifications.length === 0 && (
              <div className="notification-empty-state">
                <p>No notifications found.</p>
              </div>
            )}

            {!loading &&
              !error &&
              notifications.map((item, index) => {
                const { title, action, icon } = notificationContent(item);
                const message = getNotificationMessage(item);
                const unread = isUnreadNotification(item);

                return (
                  <div
                    key={getNotificationId(item, index)}
                    className={`notification-item ${unread ? "unread" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openNotification(item, index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openNotification(item, index);
                      }
                    }}
                  >
                    <div className="notification-item-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M10.268 21a2 2 0 0 0 3.464 0" />
                        <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
                      </svg>
                    </div>

                    <div className="notification-item-content">
                      <div className="notification-item-title">
                        <div className="notification-item-title-copy">
                          <p>{title}</p>
                          {unread && <span>New</span>}
                        </div>
                        <i className={`bi ${icon} notification-item-action-icon`} title={action} aria-label={action} />
                      </div>
                      {message && message !== title && (
                        <span className="notification-message">{message}</span>
                      )}
                      <time>{getNotificationTime(item)}</time>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="notification-footer">
            <button type="button" onClick={() => { setOpen(false); navigate("/all-notifications"); }}>
              See all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;
